/**
 * Tiện ích địa điểm cho tìm kiếm bản đồ và lọc theo tỉnh/huyện/xã.
 *
 * Cung cấp tọa độ trung tâm tỉnh/thành, chuẩn hóa tên địa danh (bỏ dấu),
 * khớp từ khóa search với tên tỉnh, và zoom mặc định theo cấp địa lý.
 */

/** Tọa độ [lat, lng] trung tâm các tỉnh/thành Việt Nam dùng cho bản đồ. */
const PROVINCE_CENTERS = {
  'An Giang': [10.5216, 105.1259],
  'Bắc Ninh': [21.1861, 106.0763],
  'Cà Mau': [9.1527, 105.1967],
  'Cao Bằng': [22.6667, 106.25],
  'Cần Thơ': [10.0452, 105.7469],
  'Đà Nẵng': [16.0544, 108.2022],
  'Đắk Lắk': [12.7103, 108.2378],
  'Điện Biên': [21.8042, 103.1077],
  'Đồng Nai': [10.9574, 106.8429],
  'Đồng Tháp': [10.493, 105.6882],
  'Gia Lai': [13.9833, 108.0],
  'Hà Nội': [21.0285, 105.8542],
  'Hà Tĩnh': [18.3559, 105.8878],
  'Hải Phòng': [20.8449, 106.6881],
  'Huế': [16.4637, 107.5909],
  'Hưng Yên': [20.6464, 106.051],
  'Khánh Hòa': [12.2388, 109.1967],
  'Lai Châu': [22.3964, 103.4583],
  'Lâm Đồng': [11.9404, 108.4583],
  'Lạng Sơn': [21.8478, 106.7577],
  'Lào Cai': [22.4833, 103.95],
  'Nghệ An': [18.6796, 105.6813],
  'Ninh Bình': [20.2506, 105.9744],
  'Phú Thọ': [21.2684, 105.2046],
  'Quảng Ngãi': [15.1213, 108.8044],
  'Quảng Ninh': [21.0064, 107.2925],
  'Quảng Trị': [16.82, 107.09],
  'Sơn La': [21.1022, 103.7289],
  'Tây Ninh': [11.3144, 106.0975],
  'Thái Nguyên': [21.5892, 105.8481],
  'Thanh Hóa': [19.8067, 105.7852],
  'Thành phố Hồ Chí Minh': [10.8231, 106.6297],
  'Hồ Chí Minh': [10.8231, 106.6297],
  'Tuyên Quang': [21.8233, 105.2142],
  'Vĩnh Long': [10.2399, 105.9722],
};

const normalizeLocationLabel = (value) => value?.trim().toLowerCase() || '';

/**
 * Chuẩn hóa tên địa danh để so sánh: lowercase, bỏ dấu, gộp khoảng trắng.
 *
 * @param {string} value
 * @returns {string}
 */
export const normalizeLocationKey = (value) =>
  (value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/g, 'd')
    .replace(/\s+/g, '');

/**
 * Tìm tên tỉnh khớp chính xác với chuỗi search (sau khi chuẩn hóa).
 *
 * @param {string} searchText
 * @returns {string|null}
 */
export const findMatchingProvince = (searchText) => {
  const searchKey = normalizeLocationKey(searchText);

  if (!searchKey) {
    return null;
  }

  const matchedEntry = Object.keys(PROVINCE_CENTERS).find(
    (name) => normalizeLocationKey(name) === searchKey
  );

  return matchedEntry || null;
};

/**
 * Đọc bộ lọc địa điểm từ query string.
 *
 * @param {URLSearchParams} searchParams
 * @returns {{ province: string, district: string, ward: string }}
 */
export const parseLocationSearchFilters = (searchParams) => ({
  province: searchParams.get('province') || '',
  district: searchParams.get('district') || '',
  ward: searchParams.get('ward') || '',
});

/**
 * Hợp nhất filter địa điểm từ URL với tỉnh suy ra từ ô search (nếu khác province đang chọn).
 *
 * @param {URLSearchParams} searchParams
 * @returns {{ province: string, district: string, ward: string }}
 */
export const resolveMapLocationSearch = (searchParams) => {
  const locationSearch = parseLocationSearchFilters(searchParams);
  const searchText = searchParams.get('search') || '';
  const matchedProvince = findMatchingProvince(searchText);

  if (!matchedProvince) {
    return locationSearch;
  }

  const matchedKey = normalizeLocationKey(matchedProvince);
  const selectedKey = normalizeLocationKey(locationSearch.province);

  if (!selectedKey || matchedKey === selectedKey) {
    return locationSearch;
  }

  // Tỉnh từ search thay thế province hiện tại, reset huyện/xã
  return {
    province: matchedProvince,
    district: '',
    ward: '',
  };
};

/**
 * Kiểm tra có ít nhất một cấp địa lý trong bộ lọc hay không.
 *
 * @param {{ province?: string, district?: string, ward?: string }} locationSearch
 * @returns {boolean}
 */
export const hasLocationSearch = (locationSearch) =>
  Boolean(locationSearch?.province || locationSearch?.district || locationSearch?.ward);

const matchesLocationValue = (propertyValue, filterValue) => {
  if (!filterValue) {
    return true;
  }

  return normalizeLocationLabel(propertyValue) === normalizeLocationLabel(filterValue);
};

/**
 * Lọc danh sách BĐS theo province/district/ward (so khớp không phân biệt hoa thường).
 *
 * @param {Array} properties
 * @param {Object} locationSearch
 * @returns {Array}
 */
export const filterPropertiesByLocationSearch = (properties, locationSearch) => {
  if (!hasLocationSearch(locationSearch)) {
    return properties;
  }

  return properties.filter(
    (property) =>
      matchesLocationValue(property.province, locationSearch.province) &&
      matchesLocationValue(property.district, locationSearch.district) &&
      matchesLocationValue(property.ward, locationSearch.ward)
  );
};

/**
 * Lấy tọa độ trung tâm tỉnh; hỗ trợ khớp tên không phân biệt hoa thường.
 *
 * @param {string} provinceName
 * @returns {[number, number]|null}
 */
export const getProvinceCenter = (provinceName) => {
  if (!provinceName) {
    return null;
  }

  if (PROVINCE_CENTERS[provinceName]) {
    return PROVINCE_CENTERS[provinceName];
  }

  const normalizedName = normalizeLocationLabel(provinceName);
  const matchedEntry = Object.entries(PROVINCE_CENTERS).find(
    ([name]) => normalizeLocationLabel(name) === normalizedName
  );

  return matchedEntry ? matchedEntry[1] : null;
};

/**
 * Mức zoom bản đồ theo cấp địa lý đang lọc (xã > huyện > tỉnh).
 *
 * @param {Object} locationSearch
 * @returns {number|null}
 */
export const getLocationSearchZoom = (locationSearch) => {
  if (locationSearch?.ward) {
    return 13;
  }

  if (locationSearch?.district) {
    return 12;
  }

  if (locationSearch?.province) {
    return 10;
  }

  return null;
};

/**
 * View mặc định cho bản đồ khi không có marker: center tỉnh + zoom phù hợp.
 *
 * @param {Object} locationSearch
 * @returns {{ center: [number, number], zoom: number }|null}
 */
export const getFallbackMapView = (locationSearch) => {
  const provinceCenter = getProvinceCenter(locationSearch?.province);

  if (!provinceCenter) {
    return null;
  }

  return {
    center: provinceCenter,
    zoom: getLocationSearchZoom(locationSearch),
  };
};
